import axios, { AxiosResponse, AxiosPromise } from 'axios';
import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { CourseId } from '@/openapi/enum';
import { CourseProgress } from '@/openapi/user';

import CourseProgressMixin from '@/user/mixins/CourseProgressMixin';
import RoutesMixin from '@/user/mixins/RoutesMixin';
import StudyJapaneseMixin from '@/user/mixins/StudyJapaneseMixin';
import UserMixin from '@/user/mixins/UserMixin';
import WindowAdjustMixin from '@/user/mixins/WindowAdjustMixin';
import MasterApiUtility from '@/classes/common/MasterApiUtility';
import StorageUtility from '@/classes/common/StorageUtility';
import { USER_STORAGE_KEY } from '@/classes/common/Constants';

import ConfirmDialog from '@/user/components/dialog/confirm/ConfirmDialog.vue';
import Header from '@/user/components/header/Header.vue';
import BackButton from '@/user/components/header/backButton/BackButton.vue';
import ClearButton from '@/user/components/header/clearButton/ClearButton.vue';
import ScoreButton from '@/user/components/header/scoreButton/ScoreButton.vue';
import FontSmallButton from '@/user/components/header/fontSmallButton/FontSmallButton.vue';
import FontLargeButton from '@/user/components/header/fontLargeButton/FontLargeButton.vue';
import RestErrorBar from '@/user/components/study/restErrorBar/RestErrorBar.vue';
import RestTimeBar from '@/user/components/study/restTimeBar/RestTimeBar.vue';
import StudyResultDialog from '@/user/components/study/dialog/result/StudyResultDialog.vue';
import JapaneseWork from '@/user/pages/authorized/study/japanese/work/JapaneseWork.vue';




const INCORRECT_LIMIT = 2;

export default mixins(
  CourseProgressMixin,
  RoutesMixin,
  StudyJapaneseMixin,
  UserMixin,
  WindowAdjustMixin
).extend({

  name: 'Japanese',

  components: {
    Header,
    BackButton,
    ClearButton,
    ScoreButton,
    FontSmallButton,
    FontLargeButton,
    JapaneseWork,
    RestErrorBar,
    RestTimeBar,
    ConfirmDialog,
    StudyResultDialog,
  },

  data(): {
    visibleBackDialog: boolean;
    isFontLarge: boolean;
    clearSwitch: boolean;  // workでwatchして全消去処理を行うためだけのトグルスイッチ
    tutorialSectionNo: number;
  } {
    return {
      visibleBackDialog: false,
      isFontLarge: false,
      clearSwitch: true,
      tutorialSectionNo: 1
    };
  },

  computed: {
    isScoreFlash(): boolean {
      return (!this.isTutorial && !this.hasEmptyReplys)
        || (this.isTutorial && this.prejudgeReplys);
    },
  },

  async created(): Promise<void> {
    // font読み込み
    await this.loadFont();

    this.$_initialize();
  },

  destroyed(): void {
    this.clearRestTimeInterval();
    this.clearStudyVariable();
    this.clearStudyResult();
  },

  methods: {

    back(): void {
      this.visibleBackDialog = false;
      this.$router.push(this.routes.UnitSelect);
    },

    clear(): void {
      this.refreshReplys();
      this.clearSwitch = !this.clearSwitch;
    },

    toggleFontSize(): void {
      this.isFontLarge = !this.isFontLarge;
      StorageUtility.setSessionData<boolean>(USER_STORAGE_KEY.IS_JAPANESE_FONT_LARGE, this.isFontLarge);
    },

    score(): void {
      // 問題読み込み前は何もしない
      if (!this.isQuestionsLoaded) return;

      // 答え合わせ
      let isCorrect = true;
      const incorrectQuestionNos: number[] = [];
      // 正解ユーザー以外は答え合わせ
      if (!this.isAlwaysCorrect) {
        // 未回答は不正解
        if (this.hasEmptyReplys) {
          isCorrect = false;
        } else {
          for (let i = 0; i < this.replys.length; i++) {
            const judge = this.replys[i].judge();
            if (!judge) incorrectQuestionNos.push(i + 1);
          }

          // 不正解の解答欄のうち、最初の解答欄にフォーカスを移す
          if (incorrectQuestionNos.length > 0) {
            isCorrect = false;
            // indexを渡すため、questionNo - 1
            this.setFocus(incorrectQuestionNos[0] - 1);
          }
        }
      }

      // 誤答の学習メモを保存
      if (!isCorrect) {
        const memo = incorrectQuestionNos.join(';');
        this.addStudyMemo(memo);
      }

      // 不正解回数の更新
      if (!this.isTutorial && !this.hasEmptyReplys && !isCorrect) this.incrementIncorretCount();

      // 学習結果指定、結果モーダルを開く
      this.judgeScoreResult(this.hasEmptyReplys, isCorrect, INCORRECT_LIMIT);
    },

    // StudyMixinを上書き、学習結果保存処理用
    isCourseComplete(isCorrect: boolean): boolean {
      // 不正解の場合はfalse
      if (!isCorrect) return false;

      // 回答中のユニット以外の全単元、ユニットが終了しているか
      for (const section of this.sections) {
        const progress  = this.progresses.find(s => s.sectionNo === section.sectionNo);
        // 単元内のユニット数（回答中の単元の場合はユニット数を減らしておく）
        const unitCount = section.sectionNo === this.studySectionNo ? section.unitCount - 1 : section.unitCount;
        // 単元の進捗が存在しない、もしくは単元内のユニット数より少ない場合はfalse
        if (progress === undefined || progress.unitNos.length < unitCount) return false;
      }

      return true;
    },

    // StudyMixinを上書き、学習結果保存処理用
    updateProgress(isCorrect: boolean): void {
      // 不正解の場合はreturn
      if (!isCorrect) return;

      // 進捗に学習中のユニットがない場合は追加
      const progress = this.getProgress();
      let   unitNos  = progress.unitNos;
      if (!unitNos.includes(this.studyUnitNo)) {
        unitNos.push(this.studyUnitNo);
        unitNos = _.sortBy(progress.unitNos);
      }

      // 進捗に学習中の単元がない場合は追加
      let progresses = this.progresses;
      if (!progresses.some(p => p.sectionNo === this.studySectionNo)) {
        progresses.push(progress);
        progresses = _.sortBy(progresses, p => p.sectionNo);
        this.setProgresses(progresses);
      }
    },

    // StudyMixinを上書き、学習結果保存処理用
    getProgress(): CourseProgress {
      const progress = this.progresses.find(p => p.sectionNo === this.studySectionNo);
      return progress ?? { sectionNo: this.studySectionNo, unitNos: [] };
    },

    // StudyMixinを上書き、学習終了後に学習続行用
    initializeCourseComponent(): void {
      this.initializeStudyVariable();
      this.$_initialize();
    },

    async $_initialize(): Promise<void> {
      // 進捗、セクション読込
      await this.loadProgressesState(CourseId.japanese);

      // 初期化
      this.initializeStudyVariable();
      this.initializeStudyRequest();
      this.visibleBackDialog = false;
      // this.isFontLarge = false, // 初期化しない
      // this.clearSwitch = true; // 初期化しない

      // ストレージに文字サイズ設定があれば読み込み
      const isFontLarge = StorageUtility.getSessionData<boolean>(USER_STORAGE_KEY.IS_JAPANESE_FONT_LARGE);
      if (isFontLarge !== null) this.isFontLarge = isFontLarge;

      // 回答可能回数
      this.setIncorretLimit(INCORRECT_LIMIT);

      // 問題取得
      await this.loadQuestions();
      await this.$_addQuestionText();

      // ステージ名設定
      const stageName = this.$_createStageName();
      this.setStageName(stageName);

      // チュートリアルは開始モーダル無し
      this.$_startQuestion();
    },

    $_startQuestion(): void {
      this.initializeQuestionIndex();
      this.nextQuestion();
    },

    async $_addQuestionText(): Promise<void> {
      const questions = this.questions.filter(q => q.question.length === 0);
      if (questions.length === 0) return;

      const questionNos = questions.map(q => q.questionNo);
      const sectionNoPadded = String(this.studySectionNo).padStart(2, '0');
      const sectionCode = `section${sectionNoPadded}`;

      const createRequest = (questionNo: number, type: string): AxiosPromise<string> => {
        const questionNoStr = String(questionNo).padStart(3, '0');
        const fileName = `${sectionNoPadded}-${questionNoStr}${type}.txt`;
        return MasterApiUtility.getQuestionApi().showQuestion(CourseId.japanese, sectionCode, fileName);
      };
      const requests: AxiosPromise<string>[] = [];
      questionNos.forEach(no => ['q', 'a'].forEach(type => requests.push(createRequest(no, type))));
      const responses = await axios.all(requests);
      for (let i = 0; i < responses.length; i++) {
        let qa = '';
        let tmpText = responses[i].data;
        tmpText = tmpText.replace(/\r\n/g, '\n').replace(/[\n|\r]/g, '\\n'); // 改行を\nに置換する

        // 画像部分がある場合はURLに置換
        if (tmpText.indexOf('<') != -1) {
          const regexp  = /\<.+?\>/g;
          const imgUrls = new Array();
          const matches = tmpText.match(regexp);
          matches!.forEach(m => {
            const fileName = m.substring(1, m.length - 1);
            imgUrls.push(this.$_getQuestionImageUrl(sectionCode, fileName));
          });

          // 置換処理（置換文言と置換文言以外の文字列を結合）
          const targets = tmpText.split(regexp);
          for (let i = 0; i < imgUrls.length; i++) {
            qa += targets[i] + imgUrls[i];
          }
          qa += targets[targets.length - 1];

          // 画像がない場合はそのまま格納
        } else {
          qa = tmpText;
        }

        const question = questions[Math.floor(i / 2)];
        const type = i % 2 === 0 ? 'q' : 'a';
        question[type] = qa;
      }
    },

    $_createStageName(): string {
      const name = this.getSection(this.studySectionNo).sectionName;
      return this.isTutorial ? name : `${name} (${this.studyUnitNo})`;
    },

  }

});
